import { useEffect, useMemo, useRef } from 'react';
import { FlatList, ScrollView, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { Divider } from '@ui-kitten/components';
import lodashIsEmpty from 'lodash/isEmpty';

import Form from '../../Components/FormFields/Form';
import SubmitButton from '../../Components/FormFields/SubmitButton';
import {
  formikNames,
  textCategory,
} from '../../Components/Reservation/constants';
import { styles as reservationStyles } from '../../Components/Reservation/styles';
import ResponsiveText from '../../Components/ResponsiveText';
import ResponsiveLayout from '../../Components/ScreenWrapper/ResponsiveLayout';
import constants from '../../Config/constants';
import MenuItemHelper from '../../Helper/MenuItem';
import useGoBack from '../../Hooks/useGoBack';
import routeList from '../../Routes/list';
import { setReservationCheckoutAddOn } from '../../RTK/utility';
import { selectReservationCheckoutData } from '../../RTK/utility/selectors';
import themeStyle from '../../Theme/styles';

import Summary from '../ReservationCheckout/components/Summary';
import { ReservationCheckout as ReservationCheckoutType } from '../ReservationCheckout/types';

import AddOnContainer from './AddOnContainer';
import styles from './styles';

const ReservationCheckoutAddOns = ({ navigation }) => {
  const dispatch = useDispatch();
  const goHome = useGoBack();
  const scrollRef = useRef<ScrollView | FlatList<any>>();
  const extraGroupRef = useRef({});

  const checkoutData: ReservationCheckoutType = useSelector(
    selectReservationCheckoutData,
  );

  useEffect(() => {
    if (lodashIsEmpty(checkoutData)) {
      goHome();
    }
  }, [checkoutData, goHome]);

  const LeftComponent = useMemo(
    () => (
      <View style={reservationStyles.contentContainer}>
        {checkoutData?.addOnsGroup?.map?.((e) => (
          <View
            key={e.name}
            onLayout={(event) => {
              const layout = event.nativeEvent.layout;
              extraGroupRef.current[e.name] = layout.y;
            }}
          >
            <AddOnContainer {...e} />
          </View>
        ))}
      </View>
    ),
    [checkoutData?.addOnsGroup],
  );

  const _onProceed = (data, { setErrors, setSubmitting }) => {
    const validate = MenuItemHelper.validateSelectedExtras(
      data[formikNames.ADDONS].map((e) => e.id),
      checkoutData.addOnsGroup,
    );
    setSubmitting(false);
    if (validate.ok) {
      dispatch(setReservationCheckoutAddOn(data[formikNames.ADDONS]));
      navigation.navigate(routeList.RES_CHECKOUT);
    } else {
      const groupYpos = extraGroupRef.current[validate.groupName];
      if ('scrollToOffset' in scrollRef.current) {
        // web auto scroll
        scrollRef.current.scrollToOffset({ animated: true, offset: groupYpos });
      } else if ('scrollTo' in scrollRef.current) {
        // mobile auto scroll
        scrollRef.current.scrollTo({ animated: true, y: groupYpos });
      }
      setErrors({ [formikNames.ADDONS]: { [validate.groupName]: true } }); // set error flag of [validate.groupName] on formik
    }
  };

  if (lodashIsEmpty(checkoutData)) return null;

  return (
    <Form
      initialValues={{ [formikNames.ADDONS]: checkoutData[formikNames.ADDONS] }}
      onSubmit={_onProceed}
      enableReinitialize
    >
      <ResponsiveLayout
        title="Select offered experience"
        LeftComponent={LeftComponent}
        RightComponent={
          <View
            style={
              !constants.isWeb && [
                themeStyle.pageHorizontalSpacing,
                themeStyle.spacingBottom,
              ]
            }
          >
            <View
              style={[
                reservationStyles.detailsBox,
                themeStyle.pageHorizontalSpacing,
              ]}
            >
              <View style={themeStyle.pageVerticalSpacingMedium}>
                <ResponsiveText
                  category={textCategory.HEADING}
                  style={themeStyle.bold}
                >
                  {checkoutData.storeName}
                </ResponsiveText>
                <ResponsiveText category={textCategory.BODY}>
                  {checkoutData.itemName}
                </ResponsiveText>
              </View>
              <Divider style={themeStyle.divider} />
              <Summary
                isReservation={checkoutData.isReservation}
                withCostBreakdown
                withPrice
              />
            </View>
            <SubmitButton
              status="info"
              style={styles.submit}
              text="Review details and payment"
            />
          </View>
        }
        rightComponentWidth={404}
        onRef={(r) => (scrollRef.current = r)}
        withTitleAnimation={false}
        withFooter={false}
      />
    </Form>
  );
};

export default ReservationCheckoutAddOns;
